import type { MoveHistoryEntry } from '../../../../deps/game/moveHistory/moveHistory.types'
import { cn } from '@merels/ui'
import { useTheme } from '../../../../contexts/ThemeContext'

interface MoveSummaryProps {
  entry: MoveHistoryEntry
  isOurMove: boolean
  isAI: boolean
  moveDisplay: string
  onClick: () => void
  isLocalMode?: boolean
  isOmniMode?: boolean
}

export function MoveSummary({
  entry,
  isOurMove,
  isAI,
  moveDisplay,
  onClick,
  isLocalMode = false,
  isOmniMode = false
}: MoveSummaryProps) {
  const { getPlayerColor } = useTheme()
  
  // Determine status indicator
  const statusIndicator = entry.success 
    ? '✓' 
    : entry.error 
    ? '✗' 
    : '⏳'

  // Get player color if playerId is numeric
  const playerIdNum = entry.playerId ? parseInt(entry.playerId) : null
  const playerColor = playerIdNum !== null && !isNaN(playerIdNum) ? getPlayerColor(playerIdNum) : null
  
  if (playerColor) {
    console.log('[MoveSummary] Player color for', entry.playerId, ':', playerColor)
  }

  // Determine color scheme based on player type and status
  const getColorClasses = () => {
    if (entry.error) {
      // Error state - use danger colors
      return 'bg-danger-bg/20 border-danger-bg/40 hover:bg-danger-bg/30 text-danger'
    }
    
    if (isAI) {
      // AI moves - use accent with blue tint (could be theme color in future)
      return entry.success
        ? 'bg-accent/10 border-accent/30 hover:bg-accent/20 text-accent'
        : 'bg-accent/20 border-accent/40 hover:bg-accent/30 text-accent'
    }
    
    if (isOurMove) {
      // Our moves - use accent with green tint (could be success color in future)
      return entry.success
        ? 'bg-accent/10 border-accent/30 hover:bg-accent/20 text-accent'
        : 'bg-accent/20 border-accent/40 hover:bg-accent/30 text-accent'
    }
    
    // Other human players - use subtle background (or use player color if available)
    return entry.success
      ? 'bg-subtle border-basic hover:bg-subtle-hover text-primary'
      : 'bg-subtle-hover border-basic hover:bg-subtle text-primary'
  }

  // Get inline styles for player color if available
  const getPlayerColorStyles = (): React.CSSProperties | undefined => {
    if (entry.error || isAI || isOurMove || !playerColor) {
      return undefined
    }
    
    // Use player color for other players' moves - make it more visible
    return {
      borderColor: playerColor.primary,
      borderWidth: '2px',
      backgroundColor: playerColor.background + '80', // More opaque
      color: playerColor.primary,
      fontWeight: '600',
    }
  }

  // Format player label: "{mateName} as {playerId}" or "As {playerId}" in local mode (non-Omni)
  const getPlayerLabel = (): string => {
    if (entry.playerId) {
      // In local mode (not Omni mode), skip mate name
      if (isLocalMode && !isOmniMode) {
        return `As ${entry.playerId}`
      } else if (entry.mateName) {
        // In Omni mode or normal mode, show mate name
        return `${entry.mateName} as ${entry.playerId}`
      } else {
        return `As ${entry.playerId}`
      }
    }
    // Fallback to just mate name if no playerId
    return entry.mateName || ''
  }

  const playerLabel = getPlayerLabel()

  return (
    <div
      className={cn(
        'rounded border text-xs cursor-pointer transition-colors px-2 py-1',
        getColorClasses()
      )}
      style={getPlayerColorStyles()}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        {playerLabel && (
          <span className="font-medium flex-shrink-0 min-w-0 truncate">
            {playerLabel}:
          </span>
        )}
        <span className="flex-1 min-w-0 truncate text-muted">
          {moveDisplay}
        </span>
        <span className={cn(
          'flex-shrink-0 text-xs',
          entry.success ? 'text-accent' : entry.error ? 'text-danger' : 'text-secondary'
        )}>
          {statusIndicator}
        </span>
        {entry.divergent && (
          <span className="flex-shrink-0 text-xs text-danger" title="Possible state divergence — see details">
            ⚠
          </span>
        )}
      </div>
    </div>
  )
}

